"use client";

import { motion, useReducedMotion } from "framer-motion";
import type { SVGProps } from "react";
type BenefitIconProps = SVGProps<SVGSVGElement>;
function SaveCoursesIcon(props: BenefitIconProps) {
  return <svg viewBox="0 0 80 80" width="80" height="80" fill="none" aria-hidden="true" {...props}>
      <path d="M40 67.5 15.2 43.7C5.8 34.7 7 19.6 17.5 13.1c7.3-4.5 16.9-2.8 22.5 3.8 5.6-6.6 15.2-8.3 22.5-3.8C73 19.6 74.2 34.7 64.8 43.7L40 67.5Z" stroke="#0B5A45" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>;
}
function TripShortlistIcon(props: BenefitIconProps) {
  return <svg viewBox="0 0 80 80" width="80" height="80" fill="none" aria-hidden="true" {...props}>
      <rect x="18" y="16" width="44" height="53" rx="4" stroke="#0B5A45" strokeWidth="2.5" />

      <path d="M31 16v-3.5A4.5 4.5 0 0 1 35.5 8h9a4.5 4.5 0 0 1 4.5 4.5V16" stroke="#0B5A45" strokeWidth="2.5" strokeLinecap="round" />

      <path d="M29 31.5 32 34.5 37 28.5" stroke="#BFFF00" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M43 32h11" stroke="#0B5A45" strokeWidth="2.3" strokeLinecap="round" />

      <path d="M29 44.5 32 47.5 37 41.5" stroke="#BFFF00" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M43 45h11" stroke="#0B5A45" strokeWidth="2.3" strokeLinecap="round" />

      <path d="M29 57.5 32 60.5 37 54.5" stroke="#BFFF00" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M43 58h11" stroke="#0B5A45" strokeWidth="2.3" strokeLinecap="round" />
    </svg>;
}
function GolfPassportIcon(props: BenefitIconProps) {
  return <svg viewBox="0 0 80 80" width="80" height="80" fill="none" aria-hidden="true" {...props}>
      <path d="M24 10h31a6 6 0 0 1 6 6v49a5 5 0 0 1-5 5H24a5 5 0 0 1-5-5V15a5 5 0 0 1 5-5Z" stroke="#0B5A45" strokeWidth="2.5" strokeLinejoin="round" />

      <path d="M24 10v60" stroke="#0B5A45" strokeWidth="2.2" />

      <path d="M29 15h27a5 5 0 0 1 5 5" stroke="#0B5A45" strokeWidth="2.2" strokeLinecap="round" />

      <circle cx="41" cy="39" r="13" stroke="#0B5A45" strokeWidth="2.2" />

      <path d="M28 39h26M41 26c4.2 4.2 6.3 8.5 6.3 13S45.2 47.8 41 52M41 26c-4.2 4.2-6.3 8.5-6.3 13S36.8 47.8 41 52" stroke="#0B5A45" strokeWidth="1.9" strokeLinecap="round" />

      <path d="M31 60h20" stroke="#B88F46" strokeWidth="2.2" strokeLinecap="round" />
    </svg>;
}
function MemberUpdatesIcon(props: BenefitIconProps) {
  return <svg viewBox="0 0 80 80" width="80" height="80" fill="none" aria-hidden="true" {...props}>
      <rect x="10" y="20" width="60" height="43" rx="5" stroke="#0B5A45" strokeWidth="2.5" />

      <path d="M13 24 36.5 43.5a5.5 5.5 0 0 0 7 0L67 24" stroke="#0B5A45" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

      <path d="m12.5 59 19-18" stroke="#B88F46" strokeWidth="2.1" strokeLinecap="round" />

      <path d="m67.5 59-19-18" stroke="#B88F46" strokeWidth="2.1" strokeLinecap="round" />
    </svg>;
}
type Benefit = {
  icon: (props: BenefitIconProps) => JSX.Element;
  title: string;
  description: string;
};
export default function Membershipbenefits() {
  const shouldReduceMotion = useReducedMotion();
  const benefits: Benefit[] = [{
    icon: SaveCoursesIcon,
    title: "Save Courses",
    description: "Keep the courses that interest you in view as you explore destinations and plan future golf trips."
  }, {
    icon: TripShortlistIcon,
    title: "Build Trip Shortlists",
    description: "Create clearer future trip ideas by bringing together courses across the destinations you are considering."
  }, {
    icon: GolfPassportIcon,
    title: "Complete Your Golf Passport",
    description: "Tell us about your preferences and we’ll help make your golf and destination inspiration more relevant."
  }, {
    icon: MemberUpdatesIcon,
    title: "Receive Relevant Member Updates",
    description: "Receive relevant course highlights, destination inspiration and TeeListClub member updates."
  }];
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.09
      }
    }
  };
  const cardVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 18
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.5,
        ease: [0.2, 0.8, 0.2, 1] as [number, number, number, number]
      }
    }
  };
  return <section data-section-id="30450" id="membershipbenefits" data-nav="dark" aria-labelledby="membershipbenefits-title" className="relative overflow-hidden bg-[#FFFDF7] text-[#073F31] ![padding:60px_0px_60px_0px]">
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-px w-[92%] max-w-[1380px] -translate-x-1/2 bg-[#DDD2C0]" />

      <div className="relative mx-auto w-full max-w-[1480px] px-5 sm:px-8 md:px-12 lg:px-16">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.35
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.5,
        ease: [0.2, 0.8, 0.2, 1]
      }} className="mx-auto mb-12 max-w-[850px] text-center md:mb-14">
          <p className="mb-5 uppercase tracking-[0.25em] text-[#B88F46] !text-sm !font-bold" style={{
          fontFamily: "'Raleway', Arial, sans-serif"
        }}><span className="!text-sm">Membership Benefits</span></p>

          <h2 id="membershipbenefits-title" className="font-normal leading-[1.08] tracking-[-0.035em] text-[#073F31]" style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(2.65rem, 4vw, 4.45rem)"
        }}>
            More reasons to join TeeListClub.
          </h2>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.12
      }} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return <motion.article key={benefit.title} data-index={index} variants={cardVariants} whileHover={shouldReduceMotion ? undefined : {
            y: -4,
            transition: {
              duration: 0.22,
              ease: [0.2, 0.8, 0.2, 1]
            }
          }} className="group relative flex min-h-[355px] flex-col items-center rounded-[14px] border border-[#DED3C1] bg-[#FFFDF9] px-7 py-10 text-center shadow-[0_10px_34px_rgba(39,55,48,0.055)] transition-[border-color,box-shadow] duration-300 hover:border-[#C7AA72] hover:shadow-[0_18px_44px_rgba(39,55,48,0.09)] md:min-h-[380px] md:px-8 md:py-11">
                <div className="mb-7 flex h-[82px] w-[82px] items-center justify-center">
                  <Icon className="h-[78px] w-[78px]" />
                </div>

                <h3 className="max-w-[255px] text-[21px] font-semibold leading-[1.23] tracking-[-0.02em] text-[#073F31] md:text-[22px]" style={{
              fontFamily: "'Raleway', Arial, sans-serif"
            }}>
                  {benefit.title}
                </h3>

                <p className="mt-6 max-w-[270px] text-[14px] font-normal leading-[1.8] text-[#60756E]" style={{
              fontFamily: "'Raleway', Arial, sans-serif"
            }}>
                  {benefit.description}
                </p>
              </motion.article>;
        })}
        </motion.div>
      </div>
    </section>;
}